/**
 * SpeechManager - Utility to manage text-to-speech functionality across components
 */

class SpeechManager {
  constructor() {
    this.activeBubbleId = null;
    this.subscribers = [];
    this.currentUtterance = null;
  }

  /**
   * Start reading a bubble
   * @param {string} bubbleId - Unique ID for the bubble
   * @returns {boolean} - Whether the reading was started
   */
  startReading(bubbleId) {
    // Always stop any existing speech synthesis first
    this.forceStopSpeech();
    
    // Set this bubble as active
    this.activeBubbleId = bubbleId;
    
    // Notify all subscribers
    this.notifySubscribers();
    return true;
  }

  /**
   * Force stop any ongoing speech synthesis
   */
  forceStopSpeech() {
    if (window.speechSynthesis) {
      // Cancel all pending utterances
      window.speechSynthesis.cancel();
      
      // Clear the current utterance
      this.currentUtterance = null;
    }
  }

  /**
   * Set the current utterance being spoken
   * @param {SpeechSynthesisUtterance} utterance - The utterance object
   */
  setCurrentUtterance(utterance) {
    this.currentUtterance = utterance;
  }

  /**
   * Stop any ongoing reading
   */
  stopReading() {
    this.forceStopSpeech();
    this.activeBubbleId = null;
    this.notifySubscribers();
  }

  /**
   * Check if a specific bubble is currently active
   * @param {string} bubbleId - Bubble ID to check
   * @returns {boolean} - Whether this bubble is active
   */
  isActive(bubbleId) {
    return this.activeBubbleId === bubbleId;
  }

  /**
   * Subscribe to changes in the active bubble
   * @param {Function} callback - Function to call when active bubble changes
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.push(callback);
    
    // Immediately notify the new subscriber of the current state
    callback(this.activeBubbleId);
    
    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  /**
   * Notify all subscribers of a change in the active bubble
   */
  notifySubscribers() {
    this.subscribers.forEach(callback => {
      callback(this.activeBubbleId);
    });
  }
}

// Export a singleton instance
const speechManager = new SpeechManager();

export default speechManager;
